import { ArrowRight } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Dialog as AriaDialog } from "react-aria-components";
import { type StatusCategory, StatusIcon, statusCategoryLabel } from "@/components/domain/StatusIcon";
import {
  Badge,
  Button,
  Callout,
  DialogBody,
  DialogFooter,
  DialogHeader,
  dataTableStyles,
  InlineError,
  Modal,
  RowSkeleton,
  Select,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { cx } from "@/lib/cx";
import { loc } from "@/lib/format";
import { toast } from "@/lib/toast";
import {
  type StatusAdminView,
  type StatusUsage,
  statusUsageQuery,
  switchProductWorkflow,
  type WorkflowAdminView,
  workflowsQuery,
} from "./api";
import { schemaError } from "./errors";
import { statusOption, WorkflowPipeline } from "./parts";

/** Where items of a status should go in the new workflow when nobody has said: same key, same name, same kind. */
const FALLBACK: Record<StatusCategory, StatusCategory[]> = {
  backlog: ["backlog", "unstarted"],
  unstarted: ["unstarted", "backlog"],
  started: ["started"],
  completed: ["completed"],
  canceled: ["canceled"],
};

const nameKey = (s: { name: unknown }) => ((s.name as { en?: string }).en ?? "").trim().toLowerCase();

/** The suggested target of one used status among the target workflow's active statuses (null: ask the person). */
export function suggestTarget(
  from: Pick<StatusAdminView, "slug" | "name" | "category">,
  targets: Pick<StatusAdminView, "id" | "slug" | "name" | "category">[],
): string | null {
  const bySlug = targets.find((t) => t.slug === from.slug);
  if (bySlug) return bySlug.id;
  const byName = targets.find((t) => nameKey(t) === nameKey(from));
  if (byName) return byName.id;
  for (const c of FALLBACK[from.category]) {
    const same = targets.find((t) => t.category === c);
    if (same) return same.id;
  }
  return null;
}

/**
 * Switch a product to another workflow. Pick the workflow, then say where the items of every status
 * in use go: each row is prefilled with a suggestion (same key, same name, else the same kind of
 * status) and nothing moves until every row has a target and the switch is confirmed. A server
 * answer that statuses are still unmapped marks those rows.
 */
export function ProductWorkflowDialog({
  productKey,
  productName,
  isOpen,
  onOpenChange,
}: {
  productKey: string;
  productName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <AriaDialog aria-labelledby="switch-workflow-title" className="flex max-h-[88dvh] flex-col outline-none">
        {({ close }) => <SwitchBody productKey={productKey} productName={productName} close={close} />}
      </AriaDialog>
    </Modal>
  );
}

function SwitchBody({
  productKey,
  productName,
  close,
}: {
  productKey: string;
  productName: string;
  close: () => void;
}) {
  const usage = useQuery(statusUsageQuery(productKey));
  const workflows = useQuery(workflowsQuery);
  const header = (
    <DialogHeader
      title={<span id="switch-workflow-title">{m.pwf_title({ product: productName })}</span>}
      onClose={close}
    />
  );

  if (usage.isPending || workflows.isPending) {
    return (
      <>
        {header}
        <DialogBody>
          <RowSkeleton rows={4} />
        </DialogBody>
      </>
    );
  }
  if (usage.isError || workflows.isError) {
    const err = usage.error ?? workflows.error;
    return (
      <>
        {header}
        <DialogBody>
          <InlineError
            message={err instanceof ApiError && err.status === 403 ? m.pwf_denied() : m.error_network()}
            onRetry={() => {
              void usage.refetch();
              void workflows.refetch();
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onPress={close}>
            {m.common_close()}
          </Button>
        </DialogFooter>
      </>
    );
  }
  return (
    <SwitchForm
      header={header}
      productKey={productKey}
      productName={productName}
      usage={usage.data}
      workflows={workflows.data}
      close={close}
    />
  );
}

function SwitchForm({
  header,
  productKey,
  productName,
  usage,
  workflows,
  close,
}: {
  header: React.ReactNode;
  productKey: string;
  productName: string;
  usage: StatusUsage;
  workflows: WorkflowAdminView[];
  close: () => void;
}) {
  const current = workflows.find((w) => w.id === usage.workflowId);
  const options = workflows.filter((w) => !w.archived && w.id !== usage.workflowId);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [unmapped, setUnmapped] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);

  const target = options.find((w) => w.id === targetId);
  const targetStatuses = useMemo(() => target?.statuses.filter((s) => !s.archived) ?? [], [target]);
  const rows = usage.statuses;
  const totalItems = rows.reduce((n, s) => n + s.itemCount, 0);

  // A new target workflow gets fresh suggestions for every row.
  useEffect(() => {
    if (!target) return;
    setMapping(Object.fromEntries(rows.map((s) => [s.id, suggestTarget(s, targetStatuses)])));
    setTouched(new Set());
    setUnmapped(new Set());
    setError(undefined);
  }, [target, targetStatuses, rows]);

  const complete = !!target && rows.every((s) => mapping[s.id]);

  async function confirm() {
    if (!target) return;
    setPending(true);
    setError(undefined);
    try {
      await switchProductWorkflow(productKey, {
        workflowId: target.id,
        statusMapping: Object.fromEntries(rows.map((s) => [s.id, mapping[s.id]!])),
      });
      toast.show({ title: m.pwf_done({ product: productName, workflow: loc(target.name) }), tone: "success" });
      close();
    } catch (e) {
      if (e instanceof ApiError && e.reason === "status_mapping_incomplete") {
        const list = (e.details?.unmapped as { statusId: string }[] | undefined) ?? [];
        setUnmapped(new Set(list.map((u) => u.statusId)));
      }
      setError(schemaError(e));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {header}
      <DialogBody className="flex flex-col gap-5">
        <p className="text-pretty">{m.pwf_intro()}</p>

        {current ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-fg-subtle">
              {m.pwf_current()}: <span className="text-fg-muted">{loc(current.name)}</span>
            </p>
            <WorkflowPipeline
              statuses={current.statuses.filter((s) => !s.archived)}
              label={m.sch_wf_statuses_of({ name: loc(current.name) })}
            />
          </div>
        ) : null}

        {options.length === 0 ? (
          <Callout tone="info">{m.pwf_no_other()}</Callout>
        ) : (
          <div className="flex flex-col gap-2">
            <Select
              label={m.pwf_target()}
              placeholder={m.pwf_target_placeholder()}
              items={options.map((w) => ({ id: w.id, label: loc(w.name) }))}
              selectedKey={targetId}
              onSelectionChange={setTargetId}
            />
            {target ? (
              <WorkflowPipeline statuses={targetStatuses} label={m.sch_wf_statuses_of({ name: loc(target.name) })} />
            ) : null}
          </div>
        )}

        {target ? (
          rows.length === 0 ? (
            <Callout tone="info">{m.pwf_no_items()}</Callout>
          ) : (
            <section aria-labelledby="pwf-map" className="flex flex-col gap-2">
              <div>
                <h3 id="pwf-map" className="text-sm font-semibold text-fg">
                  {m.pwf_map_title()}
                </h3>
                <p className="mt-0.5 text-xs text-pretty text-fg-muted">{m.pwf_map_hint()}</p>
              </div>
              <div className={dataTableStyles.frame}>
                <table className={dataTableStyles.table}>
                  <caption className="sr-only">{m.pwf_map_title()}</caption>
                  <thead className={dataTableStyles.head}>
                    <tr>
                      <th scope="col" className={dataTableStyles.th}>
                        {m.pwf_col_from()}
                      </th>
                      <th scope="col" className={cx(dataTableStyles.th, "w-20 text-right")}>
                        {m.pwf_col_items()}
                      </th>
                      <th scope="col" className={cx(dataTableStyles.th, "w-6")}>
                        <span className="sr-only">{m.pwf_col_arrow()}</span>
                      </th>
                      <th scope="col" className={cx(dataTableStyles.th, "w-72")}>
                        {m.pwf_col_to()}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => {
                      const missing = unmapped.has(s.id) && !mapping[s.id];
                      return (
                        <tr key={s.id} className={dataTableStyles.row}>
                          <th scope="row" className={cx(dataTableStyles.td, "text-left font-normal")}>
                            <span className="flex min-w-0 items-center gap-2">
                              <StatusIcon category={s.category} size={14} decorative />
                              <span className="flex min-w-0 flex-col">
                                <span className="truncate-title font-medium text-fg">{loc(s.name)}</span>
                                <span className="text-xs text-fg-subtle">{statusCategoryLabel(s.category)}</span>
                              </span>
                              {s.archived ? <Badge tone="warning">{m.fields_archived()}</Badge> : null}
                            </span>
                          </th>
                          <td className={cx(dataTableStyles.td, "text-right text-fg-muted tnum")}>{s.itemCount}</td>
                          <td className={cx(dataTableStyles.td, "px-0")}>
                            <ArrowRight size={14} aria-hidden="true" className="text-fg-subtle" />
                          </td>
                          <td className={dataTableStyles.td}>
                            <div className="flex items-start gap-2">
                              <Select
                                label={m.pwf_new_status_for({ name: loc(s.name) })}
                                labelHidden
                                placeholder={m.pwf_choose_status()}
                                items={targetStatuses.map(statusOption)}
                                selectedKey={mapping[s.id] ?? null}
                                onSelectionChange={(k) => {
                                  setMapping((x) => ({ ...x, [s.id]: k }));
                                  setTouched((t) => new Set(t).add(s.id));
                                }}
                                error={missing ? m.pwf_row_missing() : undefined}
                                className="min-w-0 flex-1"
                              />
                              {!touched.has(s.id) && mapping[s.id] ? (
                                <Badge tone="accent" className="mt-1.5">
                                  {m.pwf_suggested()}
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-pretty text-fg-muted">{m.pwf_dates_note()}</p>
            </section>
          )
        ) : null}

        {target && complete ? (
          <Callout tone="info" title={m.pwf_summary_title()}>
            {rows.length === 0
              ? m.pwf_summary_empty({ workflow: loc(target.name) })
              : m.pwf_summary({ count: totalItems, statuses: rows.length, workflow: loc(target.name) })}
          </Callout>
        ) : null}
        {error ? <InlineError message={error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onPress={close}>
          {m.common_cancel()}
        </Button>
        <Button variant="primary" isPending={pending} isDisabled={!target || !complete} onPress={() => void confirm()}>
          {m.pwf_confirm()}
        </Button>
      </DialogFooter>
    </>
  );
}
